import { PlusOutlined } from '@ant-design/icons';
import React, { useEffect, useMemo, useRef } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Checkbox, Spin } from 'antd';
import _ from 'lodash';
import { useIntersectionObserver, useMergeState } from '../../../utils/hooks';
import CreateNewUserModal from '../../components/modals/createNewUser';
import InstructionModal from '../../components/modals/instructionModal';
import NormalTable from '../../components/tables/normalTable';
import {
  deleteUser, getColumns, getSearchBy, handleFetchUsers, restoreUser,
} from './helper';
import { toastrError, toastrSuccess } from '../../../utils/toastNotification';
import displayLoading from '../../../redux/actions/loading';

import './style.scss';

const DELETE_DESCRIPTION = 'This user will be deactivated and hidden from the app. '
  + 'It stays visible here, tagged "Deleted", and can be restored at any time.';
const RESTORE_DESCRIPTION = 'This user will be reactivated and can log in again '
  + 'with their existing credentials.';

const UserManagement = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [state, setState] = useMergeState({
    isLoadingMore: false,
    users: [],
    createVisible: false,
    deleteVisible: false,
    deleteTarget: undefined,
    restoreVisible: false,
    restoreTarget: undefined,
    lastElement: undefined,
    cursor: undefined,
  });

  const searchKey = useRef();
  const includeDeletedRef = useRef(false);

  const buildFilter = (extra = {}) => {
    const filter = { includeDeleted: includeDeletedRef.current };
    if (searchKey.current) {
      Object.assign(filter, { [searchKey.current.key]: searchKey.current.value });
    }
    Object.assign(filter, extra);
    return filter;
  };

  const onDeleteClick = (record) => {
    setState({ deleteVisible: true, deleteTarget: record });
  };

  const onRestoreClick = (record) => {
    setState({ restoreVisible: true, restoreTarget: record });
  };

  const columns = useMemo(() => getColumns(onDeleteClick, onRestoreClick));
  const searchBy = useMemo(() => getSearchBy());

  const fetchInitData = async (filter, limit) => {
    dispatch(displayLoading(true));
    const users = await handleFetchUsers(filter, limit);
    setState({ users, cursor: users.length < limit ? undefined : users[users.length - 1]?.id || undefined });
    dispatch(displayLoading(false));
  };

  const onSearchOrFilter = async (keySearch) => {
    dispatch(displayLoading(true));
    searchKey.current = keySearch;
    const users = await handleFetchUsers(buildFilter(), 20);
    setState({
      users,
      cursor: users.length < 20 ? undefined : users[users.length - 1]?.id || undefined,
    });
    dispatch(displayLoading(false));
  };

  const handleLoadMore = async () => {
    if (state.isLoadingMore || !state.cursor) {
      return;
    }
    const filter = buildFilter({ cursor: state.cursor });
    setState({ isLoadingMore: true });
    const users = await handleFetchUsers(filter, 20);
    setState({
      users: [...state.users, ...users],
      cursor: users.length < 20 ? undefined : users[users.length - 1]?.id || undefined,
    });
    setState({ isLoadingMore: false });
  };

  const onToggleShowDeleted = async (e) => {
    includeDeletedRef.current = e.target.checked;
    dispatch(displayLoading(true));
    const users = await handleFetchUsers(buildFilter(), 20);
    setState({
      users,
      cursor: users.length < 20 ? undefined : users[users.length - 1]?.id || undefined,
    });
    dispatch(displayLoading(false));
  };

  const toggleCreateModal = () => {
    setState({ createVisible: !state.createVisible });
  };

  const toggleDeleteModal = () => {
    setState({ deleteVisible: !state.deleteVisible });
  };

  const toggleRestoreModal = () => {
    setState({ restoreVisible: !state.restoreVisible });
  };

  const onConfirmDelete = async () => {
    const deletedUser = await deleteUser(state.deleteTarget.id);
    if (deletedUser.isSuccess) {
      toastrSuccess('User deleted successfully');
      setState({
        users: state.users.map(user => (
          user.id === state.deleteTarget.id ? { ...user, isDeleted: true } : user
        )),
      });
    } else {
      toastrError(deletedUser.message);
    }
  };

  const onConfirmRestore = async () => {
    const restored = await restoreUser(state.restoreTarget.id);
    if (restored.isSuccess) {
      toastrSuccess('User restored successfully');
      setState({
        users: state.users.map(user => (
          user.id === state.restoreTarget.id ? { ...user, isDeleted: false } : user
        )),
      });
    } else {
      toastrError(restored.message);
    }
  };

  const onRowClick = (row) => {
    history.push(`/user-info/${row.id}`);
  };

  const onCreate = (user) => {
    setState({ users: [user, ...state.users] });
    toastrSuccess('User created successfully!');
  };

  useEffect(() => {
    fetchInitData({}, 20);
  }, []);

  useEffect(() => {
    if (!_.isEmpty(state.users)) {
      const rowElements = document.querySelectorAll('.ant-table-row-level-0');
      setState({ lastElement: rowElements[rowElements.length - 1] });
    }
  }, [state.users]);

  useIntersectionObserver(state.lastElement, handleLoadMore, () => {}, [state.users, state.cursor]);
  return (
    <div className="user-management">
      <NormalTable
        className="user-management-table"
        columns={columns}
        dataSource={state.users}
        isCreate
        isSearch
        searchBy={searchBy}
        createType="Create new user"
        icon={<PlusOutlined />}
        onCreate={toggleCreateModal}
        fetchData={onSearchOrFilter}
        onRowClick={onRowClick}
        extra={<Checkbox onChange={onToggleShowDeleted}>Show deleted accounts</Checkbox>}
      />
      <div className="loadmore">
        {state.isLoadingMore && <Spin />}
      </div>
      <CreateNewUserModal
        visible={state.createVisible}
        onCancel={toggleCreateModal}
        onCreate={onCreate}
      />
      <InstructionModal
        visible={state.deleteVisible}
        isWarning
        title="Do you want to delete this user"
        description={DELETE_DESCRIPTION}
        onConfirm={onConfirmDelete}
        onCancel={toggleDeleteModal}
      />
      <InstructionModal
        visible={state.restoreVisible}
        title={state.restoreTarget ? `Restore ${state.restoreTarget.email}?` : ''}
        description={RESTORE_DESCRIPTION}
        onConfirm={onConfirmRestore}
        onCancel={toggleRestoreModal}
      />
    </div>
  );
};

export default UserManagement;
